// The Manuscript's inline emphasis markers, read as formatting.
//
// A Manuscript node's text keeps `*italic*` and `**bold**` runs (DESIGN.md
// "Data shapes"); the alignment's `strippedSourceText` has them removed,
// because that is the side the word diff runs on. Review has to show both at
// once: the emphasis as real emphasis, and the diff's removals over the same
// characters. So this module reads the markers into runs expressed in
// *stripped* coordinates, which is the one coordinate system the diff, the
// node spans, and the painter all share.

/** How a run of characters is set. `none` is the body face. */
export type EmphasisKind = "none" | "italic" | "bold";

/** A half-open range of the stripped text, and how it is set. */
export interface EmphasisRun {
  readonly start: number;
  readonly end: number;
  readonly kind: Exclude<EmphasisKind, "none">;
}

export interface Emphasized {
  /** The text with the markers removed; character-identical to the node's `strippedText`. */
  readonly text: string;
  /** Emphasised ranges over `text`, in order and non-overlapping. */
  readonly runs: readonly EmphasisRun[];
}

/**
 * Matches the marker pairs the parsers emit. Bold first, so `**x**` is one
 * bold run rather than an empty italic run around `*x*`, and `[^*]+` leaves an
 * unpaired asterisk (a footnote mark, a multiplication sign) as text. Two
 * loose asterisks in one paragraph are read as emphasis, which is a guess —
 * but it is the same expression the alignment strips `strippedText` with, and
 * the two agreeing is what matters: a disagreement would offset every removal
 * the Manuscript column paints.
 */
const MARKER = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;

/**
 * Reads `*italic*` and `**bold**` out of a node's text into ranges over the
 * text without them. An unclosed marker stays a literal asterisk, because a
 * half-typed emphasis in a manuscript is far likelier than one the Author
 * meant to run to the end of the paragraph. The returned `text` is
 * character-identical to the node's `strippedText`, which is the coordinate
 * system the diff, the node spans, and the painter all share.
 */
export function readEmphasis(marked: string): Emphasized {
  const runs: EmphasisRun[] = [];
  let text = "";
  let last = 0;
  MARKER.lastIndex = 0;
  for (let m = MARKER.exec(marked); m !== null; m = MARKER.exec(marked)) {
    text += marked.slice(last, m.index);
    const bold = m[1];
    const inner = bold ?? m[2] ?? "";
    runs.push({
      start: text.length,
      end: text.length + inner.length,
      kind: bold ? "bold" : "italic",
    });
    text += inner;
    last = m.index + m[0].length;
  }
  text += marked.slice(last);
  return { text, runs };
}
